<script setup lang="ts">
import { Form, useField, useForm } from 'vee-validate'
import { computed, ref, watch } from 'vue'
import * as yup from 'yup'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'
import ErrorIcon from '/svg/error-icon.svg'

const props = defineProps<{
  modelValue: boolean
  type: string
}>()

const emit = defineEmits(['update:modelValue', 'confirm', 'cancel'])

const closeModal = () => {
  emit('update:modelValue', false)
  emit('cancel')
}

const toast = useToast()
// const { countries } = useSettings();
const loading = ref(false)

// استفاده از prop type به عنوان مقدار اصلی
const internationalType = ref(props.type || 'بین بانکی')

// Options
const internationalTypeOptions = [
  { label: 'بین بانکی', value: 'بین بانکی' },
  { label: 'رولوت', value: 'رولوت' },
  { label: 'پی پال', value: 'پی پال' },
  { label: 'وایز', value: 'وایز' },
]

const poundOptions = [
  { label: 'بله', value: true },
  { label: 'خیر', value: false },
]

// Watch for prop changes
watch(
  () => props.type,
  (newType) => {
    if (newType && newType !== internationalType.value) {
      internationalType.value = newType
      resetForm()
    }
  }
)

// Dynamic validation schema
const validationSchema = computed(() => {
  const baseSchema = {
    bankName: yup
      .string()
      .required('نام بانک الزامی است')
      .min(2, 'نام بانک باید حداقل ۲ کاراکتر باشد')
      .max(100, 'نام بانک باید حداکثر ۱۰۰ کاراکتر باشد')
      .matches(/^[a-zA-Z ]*$/, 'نام بانک باید فقط حروف انگلیسی باشد'),
    ownerName: yup
      .string()
      .required('نام دارنده حساب الزامی است')
      .min(3, 'نام دارنده حساب باید حداقل ۳ کاراکتر باشد')
      .max(100, 'نام دارنده حساب باید حداکثر ۱۰۰ کاراکتر باشد')
      .matches(/^[a-zA-Z ]*$/, 'نام دارنده حساب باید فقط حروف انگلیسی باشد'),
    country: yup.string().required('کشور الزامی است'),
  }

  if (internationalType.value === 'بین بانکی' || internationalType.value === 'وایز') {
    return yup.object({
      ...baseSchema,
      iban: yup
        .string()
        .required('شماره IBAN الزامی است')
        .matches(/^[A-Z]{2}[0-9]{2}[A-Z0-9]{1,30}$/, 'فرمت IBAN معتبر نیست')
        .min(15, 'شماره IBAN باید حداقل ۱۵ کاراکتر باشد')
        .max(34, 'شماره IBAN باید حداکثر ۳۴ کاراکتر باشد'),
      bic: yup
        .string()
        .required('شماره BIC الزامی است')
        .min(8, 'شماره BIC باید حداقل ۸ کاراکتر باشد')
        .max(11, 'شماره BIC باید حداکثر ۱۱ کاراکتر باشد'),
      isPound: yup.boolean().nullable(),
      sortCode: yup.string().when('isPound', {
        is: true,
        then: (schema) => schema.required('شماره Sort Code برای حساب پوند الزامی است'),
        otherwise: (schema) => schema.notRequired(),
      }),
      accountNumber: yup.string().when('isPound', {
        is: true,
        then: (schema) => schema.required('شماره Account Number برای حساب پوند الزامی است'),
        otherwise: (schema) => schema.notRequired(),
      }),
    })
  } else if (internationalType.value === 'رولوت') {
    return yup.object({
      ...baseSchema,
      iban: yup
        .string()
        .required('شماره IBAN الزامی است')
        .matches(/^[A-Z]{2}[0-9]{2}[A-Z0-9]{1,30}$/, 'فرمت IBAN معتبر نیست')
        .min(15, 'شماره IBAN باید حداقل ۱۵ کاراکتر باشد')
        .max(34, 'شماره IBAN باید حداکثر ۳۴ کاراکتر باشد'),
      bic: yup
        .string()
        .required('شماره BIC الزامی است')
        .min(8, 'شماره BIC باید حداقل ۸ کاراکتر باشد')
        .max(11, 'شماره BIC باید حداکثر ۱۱ کاراکتر باشد'),
      revolutTag: yup
        .string()
        .required('Revolut Tag الزامی است')
        .min(2, 'Revolut Tag باید حداقل ۲ کاراکتر باشد')
        .max(50, 'Revolut Tag باید حداکثر ۵۰ کاراکتر باشد'),
    })
  } else if (internationalType.value === 'پی پال') {
    return yup.object({
      ...baseSchema,
      paypalEmail: yup.string().required('ایمیل Paypal الزامی است').email('فرمت ایمیل معتبر نیست'),
      paypalId: yup.string().nullable().max(50, 'Paypal ID باید حداکثر ۵۰ کاراکتر باشد'),
    })
  }

  return yup.object({})
})

// Form setup
const { errors, resetForm } = useForm({
  validationSchema,
  initialValues: {
    isPound: null,
    country: '',
    sortCode: '',
    accountNumber: '',
    iban: '',
    bic: '',
    bankName: '',
    ownerName: '',
    revolutTag: '',
    paypalEmail: '',
    paypalId: '',
  },
})

// Fields
const { value: iban } = useField('iban')
const { value: bic } = useField('bic')
const { value: bankName } = useField('bankName')
const { value: ownerName } = useField('ownerName')
const { value: country } = useField('country')
const { value: isPound } = useField('isPound')
const { value: sortCode } = useField('sortCode')
const { value: accountNumber } = useField('accountNumber')
const { value: revolutTag } = useField('revolutTag')
const { value: paypalEmail } = useField('paypalEmail')
const { value: paypalId } = useField('paypalId')

// Computed fields for dynamic labels
const ibanField = computed({
  get: () => (internationalType.value === 'پی پال' ? paypalEmail.value : iban.value),
  set: (value) => {
    if (internationalType.value === 'پی پال') {
      paypalEmail.value = value
    } else {
      iban.value = value
    }
  },
})

const bicField = computed({
  get: () => (internationalType.value === 'پی پال' ? paypalId.value : bic.value),
  set: (value) => {
    if (internationalType.value === 'پی پال') {
      paypalId.value = value
    } else {
      bic.value = value
    }
  },
})

// Methods
// const onInternationalTypeChange = () => {
//     resetForm();
// };

const filterEnglishCharacters = (event: Event) => {
  const input = event.target as HTMLInputElement
  const englishOnlyRegex = /^[a-zA-Z ]*$/
  if (!englishOnlyRegex.test(input.value)) {
    input.value = input.value.replace(/[^a-zA-Z ]/g, '')
  }
}

// Mutations
// const { mutate: createInternationalAccount } = useMutationApi({
//     mutationKey: ["createInternationalAccount"],
//     mutationFn: createInternationalAccountAction,
//     onSuccess: () => {
//         loading.value = false;
//         toast.success("حساب بین‌المللی با موفقیت ایجاد شد", "موفقیت");
//         emit("confirm");
//         closeModal();
//     },
//     onError: (error: Error) => {
//         loading.value = false;
//         toast.error(getErrorMessage(error), "خطا");
//     },
// });

// const { mutate: createInternationalRevolutAccount } = useMutationApi({
//     mutationKey: ["createInternationalRevolutAccount"],
//     mutationFn: createInternationalRevolutAccountAction,
//     onSuccess: () => {
//         loading.value = false;
//         toast.success("حساب رولوت با موفقیت ایجاد شد", "موفقیت");
//         emit("confirm");
//         closeModal();
//     },
//     onError: (error: Error) => {
//         loading.value = false;
//         toast.error(getErrorMessage(error), "خطا");
//     },
// });

// const { mutate: createInternationalPaypalAccount } = useMutationApi({
//     mutationKey: ["createInternationalPaypalAccount"],
//     mutationFn: createInternationalPaypalAccountAction,
//     onSuccess: () => {
//         loading.value = false;
//         toast.success("حساب پی پال با موفقیت ایجاد شد", "موفقیت");
//         emit("confirm");
//         closeModal();
//     },
//     onError: (error: Error) => {
//         loading.value = false;
//         toast.error(getErrorMessage(error), "خطا");
//     },
// });

const submitForm = () => {
  loading.value = true

  // switch (internationalType.value) {
  //     case "بین بانکی":
  //         createInternationalAccount({
  //             iban_number: values.iban,
  //             bic_number: values.bic,
  //             bank_name: values.bankName,
  //             owner_full_name: values.ownerName,
  //             country_id: values.country,
  //             ...(values.isPound && {
  //                 sort_code: values.sortCode,
  //                 account_number: values.accountNumber,
  //                 is_pound: true
  //             })
  //         });
  //         break;
  //     case "رولوت":
  //         createInternationalRevolutAccount({
  //             iban_number: values.iban,
  //             bic_number: values.bic,
  //             bank_name: values.bankName,
  //             owner_full_name: values.ownerName,
  //             country_id: values.country,
  //             revolut_tag: values.revolutTag
  //         });
  //         break;
  //     case "پی پال":
  //         createInternationalPaypalAccount({
  //             paypal_email: values.paypalEmail,
  //             paypal_id: values.paypalId || undefined,
  //             bank_name: values.bankName,
  //             owner_full_name: values.ownerName,
  //             country_id: values.country
  //         });
  //         break;
  // }
}
</script>

<template>
    <Modal :model-value="props.modelValue" @update:model-value="$emit('update:modelValue', $event)" background="#FFF"
        width="700px" :closable="true">
        <div class="px-5 py-4 pb-7 text-[#1b325a]">


            <!-- International Type Selection - فقط نمایشی و غیرقابل تغییر -->
            <div class="grid grid-cols-2 gap-6">
                <div class="mb-6 cursor-not-allowed">
                    <div class=" mb-2 absolute ms-4 -mt-2 text-[10px] font-bold">نوع حساب مقصد خارجی</div>
                    <div
                        class="w-full h-[48px] rounded-xl border px-[18px] font-medium border-[#d5ddea] bg-gray-100 flex items-center text-gray-600">
                        {{internationalTypeOptions.find(opt => opt.value === internationalType)?.label ||
                            internationalType
                        }}
                    </div>
                </div>
            </div>




            <p class="bg-[#f5f5f5] rounded-xl p-2.5 text-[#2D5396] text-base font-normal mb-6">
                حساب بانکی که خریدار قصد دارد مبلغ ارزی به آن واریز شود برای مثال (حساب بانکی، پی پال، رولوت، وایز و
                ...)
            </p>

            <Form @submit="submitForm" class="space-y-6">
                <!-- PayPal Fields -->
                <div v-if="internationalType === 'پی پال'" class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div>
                        <Input v-model="ibanField" name="ibanField" label="ایمیل Paypal را وارد کنید" type="email"
                            placeholder="example@paypal.com" />
                        <div v-if="errors.paypalEmail"
                            class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] -mt-4">
                            <img :src="ErrorIcon" width="15" />
                            <span>{{ errors.paypalEmail }}</span>
                        </div>
                    </div>
                    <div>
                        <Input v-model="bicField" name="bicField"  :isTag="true" label="Paypal Id را وارد کنید" type="text"
                            placeholder="PID123456789" />
                        <div v-if="errors.paypalId"
                            class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] -mt-4">
                            <img :src="ErrorIcon" width="15" />
                            <span>{{ errors.paypalId }}</span>
                        </div>
                    </div>
                </div>

                <!-- IBAN & BIC Fields -->
                <div v-if="internationalType === 'بین بانکی' || internationalType === 'رولوت' || internationalType === 'وایز'"
                    class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div>
                        <Input v-model="ibanField" name="ibanField" label="شماره IBAN را وارد کنید" type="text"
                            placeholder="IE84REVO99036020197146" />
                        <div v-if="errors.iban"
                            class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] -mt-4">
                            <img :src="ErrorIcon" width="15" />
                            <span>{{ errors.iban }}</span>
                        </div>
                    </div>
                    <div>
                        <Input v-model="bicField" name="bicField" label="شماره BIC CODE را وارد کنید" type="text"
                            placeholder="REVOIE2D" />
                        <div v-if="errors.bic"
                            class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] -mt-4">
                            <img :src="ErrorIcon" width="15" />
                            <span>{{ errors.bic }}</span>
                        </div>
                    </div>
                </div>

                <!-- Common International Fields -->
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div>
                        <Input v-model="bankName" name="bankName" @input="filterEnglishCharacters"
                            label="نام بانک را به انگلیسی وارد کنید" type="text" placeholder="Bank Name" />
                        <div v-if="errors.bankName"
                            class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] -mt-4">
                            <img :src="ErrorIcon" width="15" />
                            <span>{{ errors.bankName }}</span>
                        </div>
                    </div>
                    <div>
                        <Input v-model="ownerName" name="ownerName" @input="filterEnglishCharacters"
                            label="نام دارنده حساب را به انگلیسی وارد کنید" type="text" placeholder="Full Name" />
                        <div v-if="errors.ownerName"
                            class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] -mt-4">
                            <img :src="ErrorIcon" width="15" />
                            <span>{{ errors.ownerName }}</span>
                        </div>
                    </div>
                </div>

                <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div>
                        <CountrySelect v-model="country" name="country"
                            label="کشور مقصد حساب ارزی خود را انتخاب کنید" />
                        <div v-if="errors.country"
                            class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] -mt-4">
                            <img :src="ErrorIcon" width="15" />
                            <span>{{ errors.country }}</span>
                        </div>
                    </div>

                    <div v-if="internationalType === 'بین بانکی' || internationalType === 'وایز'" class="pt-3">
                        <Radio v-model="isPound" name="isPound" label="آیا حساب شما پوند است؟"
                            :options="poundOptions" />
                    </div>

                    <!-- Revolut Specific Field -->
                    <div v-if="internationalType === 'رولوت'">
                        <Input v-model="revolutTag" name="revolutTag" :isTag="true" label="Revolut Tag را وارد کنید"
                            type="text" placeholder="@username" />
                        <div v-if="errors.revolutTag"
                            class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] -mt-4">
                            <img :src="ErrorIcon" width="15" />
                            <span>{{ errors.revolutTag }}</span>
                        </div>
                    </div>
                </div>

                <!-- Bank Transfer Specific Fields -->
                <div v-if="isPound === true && (internationalType === 'بین بانکی' || internationalType === 'وایز')"
                     class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div>
                        <Input v-model="accountNumber" name="accountNumber" label="مقدار Account Number را وارد کنید"
                            type="text" placeholder="12345678" />
                        <div v-if="errors.accountNumber"
                            class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] -mt-4">
                            <img :src="ErrorIcon" width="15" />
                            <span>{{ errors.accountNumber }}</span>
                        </div>
                    </div>
                    <div>
                        <Input v-model="sortCode" name="sortCode" label="مقدار Sort Code را وارد کنید" type="text"
                            placeholder="123456" />
                        <div v-if="errors.sortCode"
                            class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] -mt-4">
                            <img :src="ErrorIcon" width="15" />
                            <span>{{ errors.sortCode }}</span>
                        </div>
                    </div>
                </div>

                <!-- PayPal Notice -->
                <div v-if="internationalType === 'پی پال'" class="-mb-1 mt-4">
                    مهم: به هیچ وجه از حساب بیزینسی استفاده نکنید.
                </div>

                <div class="flex gap-3 pt-4">
                    <Button type="submit" :disabled="loading" class="px-6">
                        {{ loading ? "در حال ثبت..." : "ثبت و افزودن حساب بانکی" }}
                    </Button>
                    <Button type="button"
                        class="border-2 border-[#2d5396] text-[#2d5396] bg-transparent px-6 hover:bg-gray-100"
                        @click="closeModal">
                        انصراف
                    </Button>
                </div>
            </Form>
        </div>
    </Modal>
</template>

<style scoped>
.close-modal-icon {
    display: none;
}
</style>
